// Rutas de distribución: maestro de rutas de reparto (vehículo, transportista,
// placa, chofer) y sus paradas (cada cliente/contrato, con orden, hora objetivo y
// dirección de entrega). Alimenta el Despacho del día y la guía de remisión SRI.
// Catering → Logística → Rutas de distribución.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Route, Truck, MapPin, Plus, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { fieldCls } from '@/features/inventario/shared';
import { cat, catRpc, invalidateCatering } from './shared';

interface Parada {
  id: string; contrato_id: string; contrato: string; cliente: string;
  orden: number; hora_objetivo: string | null; direccion_entrega: string | null; referencia: string | null;
}
interface Ruta {
  id: string; codigo: string | null; nombre: string; vehiculo: string | null; placa: string | null;
  transportista_id: string | null; transportista: string | null; chofer: string | null;
  notas: string | null; es_activo: boolean; paradas: Parada[];
}
interface Transportista { id: string; razon_social: string; placa: string | null }
interface ContratoOpt { id: string; nombre: string }

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };

export function RutasCatering() {
  const qc = useQueryClient();
  const [selId, setSelId] = useState('');
  const [editRuta, setEditRuta] = useState<Partial<Ruta> | null>(null);
  const [nuevaParada, setNuevaParada] = useState<{ contrato_id: string; orden: string; hora_objetivo: string; direccion_entrega: string } | null>(null);

  const invalidar = () => invalidateCatering(qc);

  const { data: rutas = [] } = useQuery<Ruta[]>({
    queryKey: ['cat-rutas'], queryFn: () => catRpc<Ruta[]>('cat_rutas_listar'),
  });
  const { data: transportistas = [] } = useQuery<Transportista[]>({
    queryKey: ['fin-transportistas'],
    queryFn: async () => (await apiClient.post('/rpc/fin_transportistas', {}, FIN)).data,
  });
  const { data: contratos = [] } = useQuery<ContratoOpt[]>({
    queryKey: ['cat-contratos-opt'],
    queryFn: () => cat.list<ContratoOpt>('contratos', '?select=id,nombre&deleted_at=is.null&es_activo=eq.true&order=nombre'),
  });

  const sel = useMemo(() => rutas.find((r) => r.id === selId) ?? null, [rutas, selId]);

  const guardarRuta = useMutation({
    mutationFn: (r: Partial<Ruta>) => catRpc('cat_ruta_guardar', {
      _data: {
        nombre: r.nombre, vehiculo: r.vehiculo ?? '', placa: r.placa ?? '',
        transportista_id: r.transportista_id ?? '', chofer: r.chofer ?? '', notas: r.notas ?? '',
        es_activo: r.es_activo ?? true,
      }, _id: r.id ?? null,
    }),
    onSuccess: () => { toast.success('Ruta guardada'); setEditRuta(null); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const guardarParada = useMutation({
    mutationFn: (p: { ruta_id: string; contrato_id: string; orden: string; hora_objetivo: string; direccion_entrega: string }) =>
      catRpc('cat_ruta_parada_guardar', {
        _data: {
          ruta_id: p.ruta_id, contrato_id: p.contrato_id, orden: parseInt(p.orden || '1', 10),
          hora_objetivo: p.hora_objetivo || '', direccion_entrega: p.direccion_entrega || '',
        },
      }),
    onSuccess: () => { toast.success('Parada agregada'); setNuevaParada(null); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const quitarParada = useMutation({
    mutationFn: (id: string) => catRpc('cat_ruta_parada_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Parada eliminada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-5">
      {/* Lista de rutas */}
      <Card className="lg:col-span-2">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base"><Route className="h-4 w-4" /> Rutas de reparto</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setEditRuta({ es_activo: true })}><Plus className="mr-1 h-3.5 w-3.5" /> Nueva</Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {rutas.map((r) => (
            <button key={r.id} onClick={() => setSelId(r.id)}
              className={`w-full rounded-md border px-3 py-2 text-left text-sm hover:bg-accent ${selId === r.id ? 'border-primary bg-accent' : ''} ${!r.es_activo ? 'opacity-50' : ''}`}>
              <span className="flex items-center justify-between">
                <span className="font-medium">{r.nombre}</span>
                <span className="text-xs text-muted-foreground">{r.codigo}</span>
              </span>
              <span className="flex items-center gap-2 pt-0.5 text-xs text-muted-foreground">
                <Truck className="h-3 w-3" /> {r.vehiculo || 'sin vehículo'}{r.placa ? ` · ${r.placa}` : ''} · {r.paradas.length} parada(s)
              </span>
            </button>
          ))}
          {!rutas.length && <p className="text-sm text-muted-foreground">Sin rutas. Crea la primera.</p>}
        </CardContent>
      </Card>

      {/* Detalle / edición */}
      <Card className="lg:col-span-3">
        <CardHeader className="pb-2"><CardTitle className="text-base">{editRuta ? (editRuta.id ? 'Editar ruta' : 'Nueva ruta') : 'Detalle de la ruta'}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {editRuta ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <input className={`${fieldCls} sm:col-span-2`} placeholder="Nombre de la ruta *" value={editRuta.nombre ?? ''} onChange={(e) => setEditRuta({ ...editRuta, nombre: e.target.value })} />
              <input className={fieldCls} placeholder="Vehículo" value={editRuta.vehiculo ?? ''} onChange={(e) => setEditRuta({ ...editRuta, vehiculo: e.target.value })} />
              <input className={fieldCls} placeholder="Placa" value={editRuta.placa ?? ''} onChange={(e) => setEditRuta({ ...editRuta, placa: e.target.value.toUpperCase() })} />
              <input className={fieldCls} placeholder="Chofer" value={editRuta.chofer ?? ''} onChange={(e) => setEditRuta({ ...editRuta, chofer: e.target.value })} />
              <select className={fieldCls} value={editRuta.transportista_id ?? ''} onChange={(e) => setEditRuta({ ...editRuta, transportista_id: e.target.value })}>
                <option value="">Transporte propio</option>
                {transportistas.map((t) => <option key={t.id} value={t.id}>{t.razon_social}</option>)}
              </select>
              <input className={`${fieldCls} sm:col-span-2`} placeholder="Notas" value={editRuta.notas ?? ''} onChange={(e) => setEditRuta({ ...editRuta, notas: e.target.value })} />
              {editRuta.id && (
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input type="checkbox" checked={editRuta.es_activo ?? true} onChange={(e) => setEditRuta({ ...editRuta, es_activo: e.target.checked })} /> Activa
                </label>
              )}
              <div className="flex gap-2 sm:col-span-2">
                <Button disabled={!editRuta.nombre || guardarRuta.isPending} onClick={() => guardarRuta.mutate(editRuta)}>Guardar</Button>
                <Button variant="ghost" onClick={() => setEditRuta(null)}>Cancelar</Button>
              </div>
            </div>
          ) : !sel ? (
            <p className="text-sm text-muted-foreground">Selecciona una ruta o crea una nueva.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-medium">{sel.nombre} <span className="text-xs text-muted-foreground">({sel.codigo})</span></p>
                  <p className="text-xs text-muted-foreground">
                    <Truck className="mr-1 inline h-3 w-3" />{sel.vehiculo || 'sin vehículo'}{sel.placa ? ` · ${sel.placa}` : ''}
                    {sel.chofer ? ` · chofer ${sel.chofer}` : ''} · {sel.transportista || 'transporte propio'}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => setEditRuta(sel)}><Pencil className="mr-1 h-3.5 w-3.5" /> Editar</Button>
              </div>

              <div>
                <div className="flex items-center justify-between pb-1">
                  <p className="flex items-center gap-2 text-sm font-medium"><MapPin className="h-4 w-4" /> Paradas ({sel.paradas.length})</p>
                  <Button size="sm" variant="outline" onClick={() => setNuevaParada({ contrato_id: '', orden: String(sel.paradas.length + 1), hora_objetivo: '', direccion_entrega: '' })}>
                    <Plus className="mr-1 h-3.5 w-3.5" /> Agregar parada
                  </Button>
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-1 pr-2">#</th><th className="py-1 pr-2">Cliente</th>
                    <th className="py-1 pr-2">Hora</th><th className="py-1 pr-2">Dirección</th><th />
                  </tr></thead>
                  <tbody>
                    {sel.paradas.map((p) => (
                      <tr key={p.id} className="border-b last:border-0">
                        <td className="py-1.5 pr-2 font-mono">{p.orden}</td>
                        <td className="py-1.5 pr-2">{p.cliente}<span className="block text-xs text-muted-foreground">{p.contrato}</span></td>
                        <td className="py-1.5 pr-2 font-mono">{p.hora_objetivo?.slice(0, 5) ?? '—'}</td>
                        <td className="py-1.5 pr-2 text-xs">{p.direccion_entrega || '—'}</td>
                        <td className="py-1.5 text-right">
                          <button className="text-red-600 hover:text-red-700" onClick={() => quitarParada.mutate(p.id)}><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                    {!sel.paradas.length && <tr><td colSpan={5} className="py-2 text-sm text-muted-foreground">Sin paradas. Agrega los clientes de esta ruta.</td></tr>}
                  </tbody>
                </table>

                {nuevaParada && (
                  <div className="mt-2 grid gap-2 rounded-md border p-2 sm:grid-cols-4">
                    <select className={`${fieldCls} sm:col-span-2`} value={nuevaParada.contrato_id} onChange={(e) => setNuevaParada({ ...nuevaParada, contrato_id: e.target.value })}>
                      <option value="">Cliente / contrato…</option>
                      {contratos.filter((c) => !sel.paradas.some((p) => p.contrato_id === c.id)).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                    <input className={fieldCls} type="number" placeholder="Orden" value={nuevaParada.orden} onChange={(e) => setNuevaParada({ ...nuevaParada, orden: e.target.value })} />
                    <input className={fieldCls} type="time" value={nuevaParada.hora_objetivo} onChange={(e) => setNuevaParada({ ...nuevaParada, hora_objetivo: e.target.value })} />
                    <input className={`${fieldCls} sm:col-span-3`} placeholder="Dirección de entrega (si difiere de la del cliente)" value={nuevaParada.direccion_entrega} onChange={(e) => setNuevaParada({ ...nuevaParada, direccion_entrega: e.target.value })} />
                    <div className="flex gap-2">
                      <Button size="sm" disabled={!nuevaParada.contrato_id || guardarParada.isPending} onClick={() => guardarParada.mutate({ ...nuevaParada, ruta_id: sel.id })}>Agregar</Button>
                      <Button size="sm" variant="ghost" onClick={() => setNuevaParada(null)}>Cancelar</Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
